<div cdkDrag cdkDragRootElement=".cdk-overlay-pane">
  <app-modal-header-ui
    cdkDragHandle
    title="New Loan Account"
    i18n-title="@@newLoanAccountTitle"
  ></app-modal-header-ui>

  <div class="dialog-body">
    <div class="loan-tab-navigation">
      <app-button-toggle-ui
        (stateEmit)="navigateToState($event)"
        [buttons]="toggleStates"
      ></app-button-toggle-ui>
    </div>

    <!-- Basic form and selected product details -->
    <form
      [formGroup]="basicForm"
      [hidden]="activeTab !== 'basic'"
      class="basic-loan-form"
      [class.basic-loan-form--asset]="isAssetFinancing"
    >
      <div class="basic-fields basic-fields--identity">
        <mat-form-field class="type-field" appearance="outline">
          <mat-label i18n="@@type">Type</mat-label>
          <mat-select
            formControlName="productType"
            (selectionChange)="onAccountTypeChange()"
          >
            <mat-option value="LOAN_PRODUCT" i18n="@@loan">Loan</mat-option>
            <mat-option
              value="ASSET_FINANCING_PRODUCT"
              i18n="@@asset_financing"
            >
              Asset Financing
            </mat-option>
          </mat-select>
        </mat-form-field>

        <app-searchable-select
          class="product-field"
          [label]="getLocalizedLoanProductsLabel()"
          i18n-label="@@loanProductsLabel"
          [options]="loanProducts"
          formControlName="loanProductId"
        ></app-searchable-select>

        <mat-form-field class="purpose-field" appearance="outline">
          <mat-label i18n="@@purposeDescription">Purpose/Description</mat-label>
          <input type="text" formControlName="description" matInput />
        </mat-form-field>

        <div class="figma-field figma-field--disabled">
          <span class="figma-field-label" i18n="@@currency">Currency</span>
          <span>{{ loanCurrency }}</span>
        </div>
      </div>

      <div class="basic-fields basic-fields--asset" *ngIf="isAssetFinancing">
        <mat-form-field appearance="outline">
          <mat-label i18n="@@asset_value">Asset Value</mat-label>
          <!-- Grouped as typed ("200,000"), with no currency symbol. -->
          <input
            appCurrencyInput
            [showSymbol]="false"
            type="tel"
            formControlName="assetValue"
            matInput
            required
          />
        </mat-form-field>

        <!-- Figma 15926:26242 — one field with the type as a PREFIX panel: the
             mirror of .loan-unit-field's suffix panel, so select on the left
             and value on the right share a single outline. Both halves take
             the same grey; the panel is separated by a rule, not a fill.
             floatLabel="always" because the panel fills the left side, where
             a resting label would otherwise sit behind it. -->
        <mat-form-field
          class="premium-field pct-follow"
          appearance="outline"
          floatLabel="always"
        >
          <mat-label i18n="@@insurance_premium">Insurance Premium</mat-label>
          <!-- No placeholder: a greyed "1" beside the % suffix reads as a real
               premium, so the field looked populated before a product was
               chosen. It fills from the product's own rate instead.
               Declared BEFORE the type select, though it renders after it: the
               form field takes the first control inside it as its own and
               sends every click in the field to it. With the select first,
               clicking the value opened the dropdown and took the focus.
               The select's own clicks stop at the panel for the same reason:
               bubbling on, they would move the focus into the input. -->
          <!-- The type select fills the prefix slot, so a Flat premium carries
               the loan's currency symbol in its value instead. -->
          <!-- As a percentage the input is only as wide as its figure, so the %
               after it sits beside the number (as Interest Rate's does). -->
          <input
            appCurrencyInput
            [showSymbol]="premiumIsFlat"
            [grouping]="premiumIsFlat"
            [symbol]="getCurrencySymbol()"
            type="tel"
            formControlName="insurancePremium"
            matInput
            required
            class="rate-input"
            [style.width.ch]="premiumWidth"
          />
          <span
            matPrefix
            class="premium-unit-prefix"
            [class.is-locked]="basicForm.get('insurancePremiumType')?.disabled"
            (click)="$event.stopPropagation()"
          >
            <mat-select
              formControlName="insurancePremiumType"
              (selectionChange)="clearInsurancePremium()"
            >
              <mat-option value="PERCENTAGE_OF_ASSET_VALUE" i18n="@@percentage"
                >Percentage</mat-option
              >
              <mat-option value="FLAT" i18n="@@flat">Flat</mat-option>
            </mat-select>
          </span>
          <span
            class="rate-percent"
            *ngIf="
              basicForm.get('insurancePremiumType')?.value ===
                'PERCENTAGE_OF_ASSET_VALUE' && hasValue('insurancePremium')
            "
            >%</span
          >
          <mat-error *ngIf="rangeHint('insurancePremium')">{{
            rangeHint("insurancePremium")
          }}</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label i18n="@@total_asset_value">Total Asset Value</mat-label>
          <!-- Worked out, never typed, so it shows the control's number
               grouped rather than binding to it: the control stays a number. -->
          <input
            matInput
            disabled
            [value]="groupedFigure(basicForm.get('totalAssetValue')?.value)"
          />
        </mat-form-field>

        <!-- floatLabel="always": the field is 91px wide per Figma, and at the
             13.3px resting size "Down Payment" does not fit, so Material
             truncated it. Floated it renders at 10px and fits, and a computed
             read-only field has no use for a resting placeholder anyway. -->
        <!-- compound-label-field: the label runs past the 91px box rather than
             being cut to "Dow...". pct-follow: the % beside the figure. -->
        <mat-form-field
          class="down-payment-field compound-label-field pct-follow"
          appearance="outline"
          floatLabel="always"
        >
          <mat-label i18n="@@down_payment">Down Payment</mat-label>
          <input
            formControlName="downPaymentPercentage"
            matInput
            class="rate-input"
            [style.width.ch]="downPaymentWidth"
          />
          <span class="rate-percent" *ngIf="hasValue('downPaymentPercentage')"
            >%</span
          >
          <mat-error *ngIf="rangeHint('downPaymentPercentage')">{{
            rangeHint("downPaymentPercentage")
          }}</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label i18n="@@loan_size_net">Loan Size (Net)</mat-label>
          <!-- Shown grouped; the principal control itself stays a plain number,
               which the disbursement amount and the payload read. -->
          <input
            matInput
            disabled
            [value]="groupedFigure(basicForm.get('principal')?.value)"
          />
          <mat-hint *ngIf="loanProductRecords">
            <span i18n="@@range">Range</span>:
            {{ loanProductRecords?.minimumPrincipal | numberWithComma }} -
            {{ loanProductRecords?.maximumPrincipal | numberWithComma }}
          </mat-hint>
        </mat-form-field>
      </div>

      <div class="basic-fields basic-fields--terms">
        <mat-form-field
          *ngIf="!isAssetFinancing"
          class="loan-size-field"
          appearance="outline"
        >
          <mat-label i18n="@@loanSizeLabel">Loan Size</mat-label>
          <!-- Grouped as typed ("20,000"), with no currency symbol. -->
          <input
            appCurrencyInput
            [showSymbol]="false"
            type="tel"
            formControlName="principal"
            matInput
            placeholder=""
          />
          <mat-hint *ngIf="loanProductRecords">
            <span i18n="@@range">Range</span>:
            {{ loanProductRecords?.minimumPrincipal | numberWithComma }}
            -
            {{ loanProductRecords?.maximumPrincipal | numberWithComma }}
          </mat-hint>
          <mat-error *ngIf="rangeHint('principal')">{{
            rangeHint("principal")
          }}</mat-error>
        </mat-form-field>

        <mat-form-field
          class="loan-unit-field interest-rate-field compound-label-field"
          appearance="outline"
          floatLabel="always"
        >
          <mat-label i18n="@@interestRateLabel">Interest Rate</mat-label>
          <!-- Sized to its figure so the % can follow it directly. The sign
               sits in the input's own box: the suffix slot is the unit panel. -->
          <input
            type="tel"
            formControlName="interestRate"
            matInput
            class="rate-input"
            [style.width.ch]="interestRateWidth"
          />
          <span class="rate-percent" *ngIf="hasInterestRate">%</span>
          <span
            matSuffix
            class="loan-unit-suffix"
            [class.is-locked]="basicForm.get('interestRateUnit')?.disabled"
          >
            <mat-select formControlName="interestRateUnit">
              <mat-select-trigger>
                per
                {{ periodUnitLabel(basicForm.get("interestRateUnit")?.value) }}
              </mat-select-trigger>
              <!-- Includes Tenor: a rate for the whole loan, read "per tenor". -->
              <mat-option
                *ngFor="let unit of interestRateUnits"
                [value]="unit.value"
                >{{ unit.label }}</mat-option
              >
            </mat-select>
          </span>
          <mat-hint *ngIf="loanProductRecords">
            <span i18n="@@range">Range</span>:
            {{ loanProductRecords?.minimumInterestRate }}% -
            {{ loanProductRecords?.maximumInterestRate }}%
          </mat-hint>
          <mat-error *ngIf="rangeHint('interestRate')">{{
            rangeHint("interestRate")
          }}</mat-error>
        </mat-form-field>

        <mat-form-field
          class="loan-unit-field compound-label-field"
          appearance="outline"
          floatLabel="always"
        >
          <mat-label i18n="@@loanTermLabel">Loan Term</mat-label>
          <input
            appStrictNumbers
            type="tel"
            formControlName="loanPeriodLength"
            matInput
          />
          <span
            matSuffix
            class="loan-unit-suffix"
            [class.is-locked]="basicForm.get('loanPeriodLengthUnit')?.disabled"
          >
            <!-- Picking a term unit narrows the repayment and grace units:
                 see refreshUnitOptions. -->
            <mat-select
              formControlName="loanPeriodLengthUnit"
              (selectionChange)="refreshUnitOptions(true)"
            >
              <mat-select-trigger>
                {{
                  periodUnitLabel(
                    basicForm.get("loanPeriodLengthUnit")?.value,
                    true
                  )
                }}
              </mat-select-trigger>
              <mat-option
                *ngFor="let unit of periodUnits"
                [value]="unit.value"
                >{{ unit.label }}</mat-option
              >
            </mat-select>
          </span>
          <mat-hint *ngIf="loanProductRecords">
            <span i18n="@@range">Range</span>:
            {{ loanProductRecords?.minimumLoanPeriodLength }} -
            {{ loanProductRecords?.maximumLoanPeriodLength }}
          </mat-hint>
          <mat-error *ngIf="rangeHint('loanPeriodLength')">{{
            rangeHint("loanPeriodLength")
          }}</mat-error>
        </mat-form-field>

        <mat-form-field
          class="loan-unit-field compound-label-field"
          appearance="outline"
          floatLabel="always"
        >
          <mat-label i18n="@@repaymentFrequencyLabel"
            >Repayment Frequency</mat-label
          >
          <input
            appStrictNumbers
            type="tel"
            formControlName="loanRepaymentFrequency"
            matInput
          />
          <!-- Fixed options, like Grace Period's. The earlier select had a single
               option bound to the select's own value, resolved its selection
               before that option existed, and rendered blank. -->
          <span
            matSuffix
            class="loan-unit-suffix"
            [class.is-locked]="
              basicForm.get('loanRepaymentFrequencyPeriodUnit')?.disabled
            "
          >
            <mat-select
              formControlName="loanRepaymentFrequencyPeriodUnit"
              (selectionChange)="refreshUnitOptions(true)"
            >
              <mat-select-trigger>
                {{
                  periodUnitLabel(
                    basicForm.get("loanRepaymentFrequencyPeriodUnit")?.value,
                    true
                  )
                }}
              </mat-select-trigger>
              <mat-option
                *ngFor="let unit of repaymentFrequencyUnits"
                [value]="unit.value"
                >{{ unit.label }}</mat-option
              >
            </mat-select>
          </span>
          <mat-hint *ngIf="loanProductRecords">
            <span i18n="@@range">Range</span>:
            {{ loanProductRecords?.minimumLoanRepaymentFrequency }} -
            {{ loanProductRecords?.maximumLoanRepaymentFrequency }}
          </mat-hint>
          <mat-error *ngIf="rangeHint('loanRepaymentFrequency')">{{
            rangeHint("loanRepaymentFrequency")
          }}</mat-error>
        </mat-form-field>

        <mat-form-field
          class="loan-unit-field compound-label-field"
          appearance="outline"
          floatLabel="always"
        >
          <mat-label i18n="@@gracePeriodLabel">Grace Period</mat-label>
          <input
            appStrictNumbers
            type="tel"
            formControlName="gracePeriodLength"
            matInput
          />
          <span
            matSuffix
            class="loan-unit-suffix"
            [class.is-locked]="basicForm.get('gracePeriodUnit')?.disabled"
          >
            <mat-select formControlName="gracePeriodUnit">
              <mat-select-trigger>
                {{
                  periodUnitLabel(basicForm.get("gracePeriodUnit")?.value, true)
                }}
              </mat-select-trigger>
              <mat-option
                *ngFor="let unit of gracePeriodUnits"
                [value]="unit.value"
                >{{ unit.label }}</mat-option
              >
            </mat-select>
          </span>
          <!-- Only once a product supplies the range: the "2 - 4" this fell
               back to was a placeholder, not any product's. -->
          <mat-hint *ngIf="loanProductRecords">
            <span i18n="@@range">Range</span>:
            {{ loanProductRecords?.minimumGracePeriod ?? "-" }} -
            {{ loanProductRecords?.maximumGracePeriod ?? "-" }}
          </mat-hint>
          <mat-error *ngIf="rangeHint('gracePeriodLength')">{{
            rangeHint("gracePeriodLength")
          }}</mat-error>
        </mat-form-field>
      </div>

      <div class="account-payment-penalty">
        <div class="account-payment-penalty-toggle">
          <span i18n="@@missed_payment_penalty">Missed Payment Penalty</span>
          <mat-slide-toggle
            class="savings-toggle"
            aria-label="Missed Payment Penalty"
            i18n-aria-label="@@missed_payment_penalty"
            [class.is-locked]="penaltyLocked"
            [tabIndex]="penaltyLocked ? -1 : 0"
            [checked]="
              isPanelSwitchOn(
                'missedPaymentPenalty',
                productLatePayment.required
              )
            "
            (change)="penaltyLocked || togglePenalty()"
          ></mat-slide-toggle>
        </div>
        <mat-form-field
          appearance="outline"
          floatLabel="always"
          class="compound-label-field"
        >
          <mat-label i18n="@@calculation_basis">Calculation Basis</mat-label>
          <mat-select
            [value]="
              penaltyFieldValue(
                'missedPaymentPenaltyBasis',
                productLatePayment.type
              )
            "
            [disabled]="
              penaltyLocked ||
              !isPanelSwitchOn(
                'missedPaymentPenalty',
                productLatePayment.required
              )
            "
            (selectionChange)="
              penaltyLocked || changePenaltyBasis($event.value)
            "
          >
            <mat-option value="FLAT" i18n="@@flat">Flat</mat-option>
            <mat-option
              value="PERCENTAGE_OF_INSTALMENT"
              i18n="@@percentage_of_instalment"
              >Percentage of Instalment</mat-option
            >
          </mat-select>
        </mat-form-field>
        <mat-form-field
          appearance="outline"
          floatLabel="always"
          class="compound-label-field pct-follow"
          [class.mat-form-field-invalid]="!!penaltyAmountError"
        >
          <mat-label i18n="@@amount">Amount</mat-label>
          <!-- On Flat the currency symbol is part of the shown text ("$1,000"),
               as on the product form, so it sits with the figure; an empty box
               shows nothing. Not appStrictNumbers: it rejects every key once
               the box holds a symbol - onPenaltyAmountInput keeps the figure. -->
          <!-- On a percentage basis the input is as wide as its figure and the
               % follows it in the same box, as Interest Rate's does. -->
          <input
            matInput
            type="text"
            inputmode="decimal"
            class="rate-input"
            [style.width.ch]="penaltyAmountWidth"
            [value]="penaltyAmountText"
            [disabled]="
              penaltyLocked ||
              !isPanelSwitchOn(
                'missedPaymentPenalty',
                productLatePayment.required
              )
            "
            (input)="penaltyLocked || onPenaltyAmountInput($any($event.target))"
          />
          <span
            class="rate-percent"
            *ngIf="penaltyBasis && penaltyBasis !== 'FLAT' && hasPenaltyAmount"
            >%</span
          >
          <!-- The product's range is in the product's basis; it means nothing
               once the basis is switched, and is not checked then either.
               Not a form control, so a breach shows as a red hint and outline
               rather than through mat-error. -->
          <mat-hint *ngIf="penaltyAmountError" class="field-error-hint">{{
            penaltyAmountError
          }}</mat-hint>
          <mat-hint
            *ngIf="
              !penaltyAmountError &&
              loanProductRecords &&
              (!penaltyBasis || penaltyBasis === productLatePayment.type)
            "
          >
            <span i18n="@@range">Range</span>:
            {{ (productLatePayment.minimumAmount | number: "1.0-2") || "-" }} -
            {{ (productLatePayment.maximumAmount | number: "1.0-2") || "-" }}
          </mat-hint>
        </mat-form-field>
        <mat-form-field
          appearance="outline"
          floatLabel="always"
          class="loan-unit-field compound-label-field"
          [class.mat-form-field-invalid]="!!penaltyGraceError"
        >
          <mat-label i18n="@@gracePeriodLabel">Grace Period</mat-label>
          <input
            matInput
            appStrictNumbers
            type="tel"
            [value]="
              penaltyFieldValue(
                'missedPaymentPenaltyGracePeriod',
                productLatePayment.gracePeriod
              )
            "
            [disabled]="
              penaltyLocked ||
              !isPanelSwitchOn(
                'missedPaymentPenalty',
                productLatePayment.required
              )
            "
            (input)="
              penaltyLocked ||
                setPanelFieldValue(
                  'missedPaymentPenaltyGracePeriod',
                  $any($event.target).value
                )
            "
          />
          <span
            matSuffix
            class="loan-unit-suffix"
            [class.is-locked]="
              penaltyLocked ||
              !isPanelSwitchOn(
                'missedPaymentPenalty',
                productLatePayment.required
              )
            "
          >
            <mat-select
              [value]="
                penaltyFieldValue(
                  'missedPaymentPenaltyGracePeriodUnit',
                  productLatePayment.graceUnit
                )
              "
              [disabled]="
                penaltyLocked ||
                !isPanelSwitchOn(
                  'missedPaymentPenalty',
                  productLatePayment.required
                )
              "
              (selectionChange)="
                penaltyLocked ||
                  setPanelFieldValue(
                    'missedPaymentPenaltyGracePeriodUnit',
                    $event.value
                  )
              "
            >
              <mat-select-trigger>{{
                periodUnitLabel(
                  penaltyFieldValue(
                    "missedPaymentPenaltyGracePeriodUnit",
                    productLatePayment.graceUnit
                  ),
                  true
                )
              }}</mat-select-trigger>
              <mat-option value="DAY" i18n="@@dayOption">Day</mat-option>
              <mat-option value="WEEK" i18n="@@weekOption">Week</mat-option>
              <mat-option value="MONTH" i18n="@@monthOption">Month</mat-option>
              <mat-option value="YEAR" i18n="@@yearOption">Year</mat-option>
            </mat-select>
          </span>
          <mat-hint *ngIf="penaltyGraceError" class="field-error-hint">{{
            penaltyGraceError
          }}</mat-hint>
          <mat-hint *ngIf="!penaltyGraceError && loanProductRecords">
            <span i18n="@@range">Range</span>:
            {{ productLatePayment.minimumGracePeriod ?? "-" }} -
            {{ productLatePayment.maximumGracePeriod ?? "-" }}
          </mat-hint>
        </mat-form-field>
      </div>

      <!-- Rendered unconditionally: both panels are present from the moment the
           dialog opens, collapsed, rather than appearing once a product is
           picked. Every getter they read is null-safe (`loanProductRecords || {}`
           or optional chaining) and repaymentSchedule/scheduleTotals start
           empty, so an expanded panel with no product shows blanks, not errors. -->
      <section class="design-section">
        <button
          type="button"
          class="design-section-heading"
          (click)="otherLoanDetailsExpanded = !otherLoanDetailsExpanded"
          [attr.aria-expanded]="otherLoanDetailsExpanded"
        >
          <span i18n="@@otherLoanDetails">Other Loan Details</span>
          <mat-icon>{{
            otherLoanDetailsExpanded ? "keyboard_arrow_down" : "chevron_right"
          }}</mat-icon>
        </button>

        <div class="other-loan-details" *ngIf="otherLoanDetailsExpanded">
          <article class="detail-card amortization-card">
            <h3 i18n="@@amortization_type_label">Amortization Type</h3>
            <strong>{{ amortizationLabel }}</strong>
            <p>{{ amortizationDescription }}</p>
            <div class="detail-row">
              <span i18n="@@calculation_basis">Calculation Basis</span
              ><output class="text-sm">{{ calculationBasisLabel }}</output>
            </div>
            <div class="detail-row">
              <span i18n="@@rounding_multiple">Rounding Multiple</span
              ><output>{{
                loanProductRecords?.roundingMultiple || "-"
              }}</output>
            </div>
          </article>

          <article class="detail-card compulsory-card">
            <h3>
              <span i18n="@@compulsory_savings">Compulsory Savings</span>
              <mat-slide-toggle
                class="savings-toggle"
                aria-label="Compulsory Savings"
                i18n-aria-label="@@compulsory_savings"
                [checked]="compulsorySavingsEnabled"
                (change)="
                  panelsLocked ||
                    togglePanelSwitch(
                      'compulsorySavings',
                      requiresCompulsorySavings
                    )
                "
              ></mat-slide-toggle>
            </h3>
            <div class="detail-row">
              <mat-form-field
                *ngIf="compulsorySavingsEnabled; else savingsAmountLabel"
                appearance="outline"
                floatLabel="always"
                class="account-savings-select"
              >
                <mat-label i18n="@@amount">Amount</mat-label>
                <mat-select
                  [value]="
                    panelFieldValue(
                      'compulsorySavingsType',
                      loanProductRecords?.compulsorySavingsType === 'NONE'
                        ? 'FLAT'
                        : loanProductRecords?.compulsorySavingsType || 'FLAT'
                    )
                  "
                  [disabled]="panelsLocked || !compulsorySavingsEnabled"
                  (selectionChange)="
                    panelsLocked ||
                      setPanelFieldValue('compulsorySavingsType', $event.value)
                  "
                >
                  <mat-option value="FLAT" i18n="@@flat">Flat</mat-option>
                  <mat-option
                    value="PERCENTAGE_OF_PRINCIPAL"
                    i18n="@@percentage_of_principal"
                    >Percentage of Principal</mat-option
                  >
                  <mat-option
                    value="PERCENTAGE_OF_INSTALMENT"
                    i18n="@@percentage_of_instalment"
                    >Percentage of Instalment</mat-option
                  >
                </mat-select>
              </mat-form-field>
              <ng-template #savingsAmountLabel
                ><span i18n="@@amount">Amount</span></ng-template
              >
              <span class="panel-field"
                ><input
                  matInput
                  type="text"
                  appStrictNumbers
                  aria-label="Savings amount"
                  i18n-aria-label="@@savings_amount"
                  [value]="
                    compulsorySavingsEnabled
                      ? panelFieldValue(
                          'compulsorySavingsAmount',
                          loanProductRecords?.compulsorySavingsAmount ?? ''
                        )
                      : ''
                  "
                  (input)="
                    panelsLocked ||
                      setPanelFieldValue(
                        'compulsorySavingsAmount',
                        $any($event.target).value
                      )
                  "
                  [disabled]="panelsLocked || !compulsorySavingsEnabled"
              /></span>
            </div>
            <div class="detail-row">
              <span i18n="@@savingsAccount">Savings Account</span>
              <mat-form-field
                appearance="outline"
                class="account-savings-select account-savings-account"
              >
                <mat-select
                  aria-label="Savings Account"
                  i18n-aria-label="@@savingsAccount"
                  [value]="
                    compulsorySavingsEnabled
                      ? panelFieldValue(
                          'savingsAccount',
                          compulsorySavingsAccount
                        )
                      : ''
                  "
                  (selectionChange)="
                    panelsLocked ||
                      setPanelFieldValue('savingsAccount', $event.value)
                  "
                  [disabled]="panelsLocked || !compulsorySavingsEnabled"
                >
                  <mat-option
                    *ngIf="compulsorySavingsAccount"
                    [value]="compulsorySavingsAccount"
                    >{{ compulsorySavingsAccount }}</mat-option
                  >
                </mat-select>
              </mat-form-field>
            </div>
            <div class="detail-row">
              <span i18n="@@interest_rate">Interest Rate</span>
              <span class="panel-field"
                ><input
                  matInput
                  type="text"
                  appStrictNumbers
                  aria-label="Savings interest rate"
                  i18n-aria-label="@@savings_interest_rate"
                  [value]="
                    compulsorySavingsEnabled
                      ? withPercent(
                          panelFieldValue(
                            'savingsInterestRate',
                            loanProductRecords?.compulsorySavingsInterestRate ??
                              loanProductRecords?.savingsInterestRate ??
                              loanProductRecords?.savingsProduct
                                ?.interestRate ??
                              ''
                          )
                        )
                      : ''
                  "
                  [disabled]="panelsLocked || !compulsorySavingsEnabled"
                  (input)="
                    panelsLocked ||
                      setPanelFieldValue(
                        'savingsInterestRate',
                        $any($event.target).value
                      )
                  "
              /></span>
            </div>
            <div class="detail-row detail-row--switch">
              <span i18n="@@earlyWithdrawal">Early Withdrawal</span>
              <mat-slide-toggle
                class="savings-toggle"
                aria-label="Early Withdrawal"
                i18n-aria-label="@@earlyWithdrawal"
                [disabled]="panelsLocked || !compulsorySavingsEnabled"
                [checked]="
                  compulsorySavingsEnabled &&
                  isPanelSwitchOn(
                    'earlyWithdrawal',
                    loanProductRecords?.earlyWithdrawal
                  )
                "
                (change)="
                  panelsLocked ||
                    togglePanelSwitch(
                      'earlyWithdrawal',
                      loanProductRecords?.earlyWithdrawal
                    )
                "
              ></mat-slide-toggle>
            </div>
          </article>

          <article
            class="detail-card guarantee-card"
            [class.guarantee-card--asset]="isAssetFinancing"
          >
            <h3 i18n="@@guaranteeCollateral">Guarantee/Collateral</h3>
            <div class="guarantee-row" *ngFor="let item of guaranteeDetails">
              <span class="guarantee-row-toggle">
                <span class="guarantee-row-label">{{ item.label }}</span>
                <button
                  type="button"
                  role="switch"
                  class="mini-switch"
                  [attr.aria-label]="item.label"
                  [class.mini-switch--on]="
                    isPanelSwitchOn('guarantee:' + item.key, item.enabled)
                  "
                  [attr.aria-checked]="
                    isPanelSwitchOn('guarantee:' + item.key, item.enabled)
                  "
                  (click)="
                    panelsLocked ||
                      togglePanelSwitch('guarantee:' + item.key, item.enabled)
                  "
                >
                  <i></i>
                </button>
              </span>
              <span class="panel-field">
                <input
                  type="text"
                  [value]="
                    panelFieldValue('coverage:' + item.key, item.coverage)
                  "
                  (input)="
                    panelsLocked ||
                      setPanelFieldValue(
                        'coverage:' + item.key,
                        $any($event.target).value
                      )
                  "
                  [disabled]="
                    panelsLocked ||
                    !isPanelSwitchOn('guarantee:' + item.key, item.enabled)
                  "
                />
                <small class="panel-field-label" i18n="@@coverage"
                  >Coverage</small
                >
              </span>
            </div>
            <label *ngIf="isAssetFinancing" class="title-holding-field">
              <input [value]="titleHoldingModelLabel" disabled />
              <span i18n="@@title_holding_model">Title Holding Model</span>
            </label>
          </article>

          <article class="detail-card fees-card">
            <h3 i18n="@@fees_and_charges">Fees &amp; Charges</h3>
            <div class="fees-grid-scroll">
              <div class="fees-grid">
                <div class="fees-grid-row fees-grid-header">
                  <span>Fee/Charge Name</span>
                  <span>Value</span>
                  <span>Minimum</span>
                  <span>Maximum</span>
                  <span>Event</span>
                </div>

                <div
                  class="fees-grid-row fees-grid-data"
                  *ngFor="let fee of displayedProductFees"
                >
                  <span class="fee-control fee-name">{{ fee.name }}</span>
                  <span class="fee-control fee-amount">{{
                    formatProductAmount(fee.value)
                  }}</span>
                  <span class="fee-control fee-amount">{{
                    formatProductAmount(fee.minimum)
                  }}</span>
                  <span class="fee-control fee-amount">{{
                    formatProductAmount(fee.maximum)
                  }}</span>

                  <span
                    class="fee-event fee-event--daily"
                    *ngIf="fee.eventType !== 'QUARTERLY'"
                  >
                    <span>{{ fee.event }}</span>
                    <span>{{ fee.eventTime }}</span>
                  </span>

                  <span
                    class="fee-event fee-event--quarterly"
                    *ngIf="fee.eventType === 'QUARTERLY'"
                  >
                    <span class="fee-quarterly-label">{{ fee.event }}</span>
                    <span class="fee-quarter"><small>Q1</small>Jan</span>
                    <span class="fee-quarter"><small>Q2</small>Apr</span>
                    <span class="fee-quarter"><small>Q3</small>Jul</span>
                    <span class="fee-quarter"><small>Q4</small>Oct</span>
                  </span>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section class="design-section calculator-section">
        <!-- Title, Calculate, chevron: the loan product calculator's order. The
             title and the chevron both toggle; Calculate sits between them
             rather than inside the heading, as a button cannot hold another. -->
        <div class="calculator-heading">
          <button
            type="button"
            class="design-section-heading"
            (click)="calculatorExpanded = !calculatorExpanded"
            [attr.aria-expanded]="calculatorExpanded"
          >
            <span i18n="@@live_calculator">Live Calculator</span>
          </button>
          <!-- Only once an edit has left the schedule behind the form. -->
          <button
            *ngIf="calculatorExpanded && (scheduleOutOfDate || calculating)"
            type="button"
            class="calc-run"
            (click)="calculateSchedule(true)"
            [disabled]="calculating"
          >
            <span *ngIf="!calculating" i18n="@@calculate">Calculate</span>
            <span *ngIf="calculating" i18n="@@calculating">Calculating</span>
          </button>
          <button
            type="button"
            class="calculator-toggle"
            (click)="calculatorExpanded = !calculatorExpanded"
            [attr.aria-expanded]="calculatorExpanded"
            aria-label="Live Calculator"
            i18n-aria-label="@@live_calculator"
          >
            <mat-icon>{{
              calculatorExpanded ? "keyboard_arrow_down" : "chevron_right"
            }}</mat-icon>
          </button>
        </div>
        <div class="schedule-wrap" *ngIf="calculatorExpanded">
          <table class="schedule-table">
            <!-- The loan currency's symbol under each heading, as the loan
                 product calculator shows it. -->
            <thead>
              <tr>
                <th>Period</th>
                <th>
                  Opening Balance<br /><small>{{ getCurrencySymbol() }}</small>
                </th>
                <th>
                  Principal<br /><small>{{ getCurrencySymbol() }}</small>
                </th>
                <th>
                  Interest<br /><small>{{ getCurrencySymbol() }}</small>
                </th>
                <th>
                  Closing Balance<br /><small>{{ getCurrencySymbol() }}</small>
                </th>
                <th>
                  Fee/Charges<br /><small>{{ getCurrencySymbol() }}</small>
                </th>
                <th>
                  Compulsory Savings<br /><small>{{
                    getCurrencySymbol()
                  }}</small>
                </th>
                <th>
                  Instalment Total<br /><small>{{ getCurrencySymbol() }}</small>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of repaymentSchedule">
                <td>{{ row.period }}</td>
                <td>{{ row.opening | formatNumber }}</td>
                <td>{{ row.principal | formatNumber }}</td>
                <td>{{ row.interest | formatNumber }}</td>
                <td>{{ row.closing | formatNumber }}</td>
                <td>{{ row.fees | formatNumber }}</td>
                <td>{{ row.savings | formatNumber }}</td>
                <td>
                  <strong>{{ row.total | formatNumber }}</strong>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td></td>
                <td>{{ scheduleTotals.principal | formatNumber }}</td>
                <td>{{ scheduleTotals.interest | formatNumber }}</td>
                <td></td>
                <td>{{ scheduleTotals.fees | formatNumber }}</td>
                <td>{{ scheduleTotals.savings | formatNumber }}</td>
                <td>{{ scheduleTotals.total | formatNumber }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </form>

    <!-- Disbursement Bank Details Form -->

    <form
      [formGroup]="disbursementForm"
      class="p-[10px] md:p-0"
      *ngIf="false"
      [hidden]="activeTab !== 'disbursementDetails'"
    >
      <div class="w-full">
        <div
          class="my-8 grid grid-cols-2 gap-4 md:gap-7"
          [ngClass]="
            isCorporate && selectedTransferType === 'THIRD_PARTY'
              ? 'md:grid-cols-2'
              : 'md:grid-cols-3'
          "
        >
          <!-- Transfer Type Dropdown -->
          <mat-form-field
            style="width: 100%"
            class="w-full col-span-2 md:col-span-1"
            appearance="outline"
          >
            <mat-label>Transfer Type</mat-label>
            <mat-select formControlName="transferType">
              <mat-option
                *ngFor="let type of transferTypeOptions"
                [value]="type.value"
              >
                {{ type.label }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <!-- Savings Account Section -->
          <ng-container *ngIf="selectedTransferType === 'SAVINGS_ACCOUNT'">
            <mat-form-field
              style="width: 100%"
              class="w-full"
              appearance="outline"
            >
              <mat-label>Savings Account to Credit</mat-label>
              <mat-select formControlName="savingsAccountId">
                <mat-option
                  *ngFor="let account of ownAccount"
                  [value]="account.id"
                >
                  {{ formatAccountDisplay(account) }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <!-- Display Amount (Read-Only) -->
            <mat-form-field
              style="width: 100%"
              class="w-full bg-gray-200"
              appearance="outline"
            >
              <mat-label>Disbursement Amount</mat-label>
              <!-- <span matPrefix>{{ getCurrencySymbol() }}&nbsp;</span> -->
              <input
                matInput
                [value]="getFormattedCurrency(basicForm.get('principal').value)"
                readonly
                class="bg-gray-50"
              />
            </mat-form-field>
          </ng-container>

          <!-- Third Party Account Section -->
          <ng-container *ngIf="selectedTransferType === 'THIRD_PARTY'">
            <!-- ── CORPORATE MODE: account selector dropdown ── -->
            <ng-container *ngIf="isCorporate">
              <mat-form-field
                style="width: 100%"
                class="w-full"
                appearance="outline"
              >
                <mat-label>Select Account</mat-label>
                <mat-select
                  [(ngModel)]="selectedCorporateAccountId"
                  [ngModelOptions]="{ standalone: true }"
                  (selectionChange)="onCorporateAccountSelected($event.value)"
                >
                  <mat-option
                    *ngFor="let acc of corporateAccounts"
                    [value]="acc.id"
                  >
                    {{ acc.accountNumber }} – {{ acc.accountName }} ({{
                      acc.bankName
                    }})
                  </mat-option>
                  <mat-option value="others"
                    >Others (enter manually)</mat-option
                  >
                </mat-select>
              </mat-form-field>

              <!-- Corporate – real account selected: readonly account number -->
              <mat-form-field
                *ngIf="
                  selectedCorporateAccountId &&
                  selectedCorporateAccountId !== 'others'
                "
                style="width: 100%"
                class="w-full bg-gray-200"
                appearance="outline"
              >
                <mat-label>Account Number</mat-label>
                <input
                  matInput
                  formControlName="accountNumber"
                  readonly
                  class="bg-gray-50"
                />
              </mat-form-field>

              <!-- Corporate – "Others" selected: editable account number -->
              <mat-form-field
                *ngIf="selectedCorporateAccountId === 'others'"
                style="width: 100%"
                class="w-full"
                appearance="outline"
                [class.mat-form-field-invalid]="errorMsg"
              >
                <mat-label>Account Number</mat-label>
                <input
                  matInput
                  formControlName="accountNumber"
                  type="tel"
                  appStrictNumbers
                  maxlength="10"
                />
              </mat-form-field>
            </ng-container>

            <!-- ── NON-CORPORATE MODE: editable account number input ── -->
            <mat-form-field
              *ngIf="!isCorporate"
              style="width: 100%"
              class="w-full"
              appearance="outline"
              [class.mat-form-field-invalid]="errorMsg"
            >
              <mat-label>Account Number</mat-label>
              <input
                matInput
                formControlName="accountNumber"
                type="tel"
                appStrictNumbers
                maxlength="10"
              />
            </mat-form-field>

            <!-- Bank Name: readonly when a known account is selected -->
            <mat-form-field
              *ngIf="
                isUsingDefaultAccount &&
                (!isCorporate || selectedCorporateAccountId !== null)
              "
              style="width: 100%"
              class="w-full"
              appearance="outline"
            >
              <mat-label>Bank Name</mat-label>
              <input
                matInput
                [value]="getBankNameDisplay()"
                readonly
                class="bg-gray-50"
              />
            </mat-form-field>

            <!-- Bank Name: dropdown for manual entry -->
            <mat-form-field
              *ngIf="
                !isUsingDefaultAccount &&
                (!isCorporate || selectedCorporateAccountId !== null)
              "
              style="width: 100%"
              class="w-full"
              appearance="outline"
            >
              <mat-label>Bank Name</mat-label>
              <mat-select formControlName="bankId">
                <mat-option *ngFor="let bank of banks" [value]="bank.code">
                  {{ bank.name }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </ng-container>
        </div>

        <!-- Second Row for Third Party Account -->
        <div
          class="mb-8 grid grid-cols-2 md:grid-cols-2 gap-4 md:gap-7"
          *ngIf="selectedTransferType === 'THIRD_PARTY'"
        >
          <mat-form-field
            style="width: 100%"
            class="w-full bg-gray-200"
            appearance="outline"
            [class.mat-form-field-invalid]="errorMsg"
          >
            <mat-label>Account Name </mat-label>
            <input
              matInput
              formControlName="accountName"
              placeholder="Enter account name"
              readonly
            />

            <!-- Error Message -->
            <mat-error *ngIf="errorMsg">
              {{ errorMsg }}
            </mat-error>
          </mat-form-field>

          <!-- Display Amount (Read-Only) -->
          <mat-form-field
            style="width: 100%"
            class="w-full bg-gray-200"
            appearance="outline"
          >
            <mat-label>Disbursement Amount</mat-label>
            <!-- <span matPrefix>{{ getCurrencySymbol() }}&nbsp;</span> -->
            <input
              matInput
              [value]="getFormattedCurrency(basicForm.get('principal').value)"
              readonly
              class="bg-gray-50"
            />
          </mat-form-field>
        </div>
      </div>
    </form>

    <form
      [formGroup]="disbursementForm"
      class="disbursement-form"
      [hidden]="activeTab !== 'disbursementDetails'"
    >
      <div
        class="disbursement-card"
        [class.disbursement-card--vendor]="isApprovedVendorDisbursement"
        [class.disbursement-card--borrower]="
          isAssetFinancing && !isApprovedVendorDisbursement
        "
      >
        <ng-container *ngIf="isApprovedVendorDisbursement">
          <div class="vendor-toolbar">
            <mat-form-field class="vendor-method-field" appearance="outline">
              <mat-label>Disbursement Method</mat-label>
              <mat-select formControlName="disbursementMethod">
                <mat-option
                  *ngFor="let method of disbursementMethods"
                  [value]="method.value"
                >
                  {{ method.label }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <label class="vendor-search">
              <mat-icon>search</mat-icon>
              <input
                type="search"
                [formControl]="vendorSearchControl"
                placeholder="Search"
              />
            </label>
          </div>

          <div class="vendor-table-shell">
            <div class="vendor-table-account">
              <div class="vendor-table-row vendor-table-header" role="row">
                <span class="vendor-radio-spacer"></span>
                <span i18n="@@vendor_dealer_name">Vendor/Dealer Name</span>
                <span i18n="@@location">Location</span>
                <span i18n="@@contact_person">Contact Person</span>
                <span i18n="@@telephone_number">Telephone No.</span>
                <span i18n="@@email_address">Email Address</span>
              </div>
              <div
                class="vendor-table-row"
                role="radio"
                *ngFor="let vendor of filteredVendorDealers"
                [attr.aria-checked]="selectedVendorId === vendor.id"
                (click)="selectVendor(vendor)"
              >
                <span
                  class="vendor-radio"
                  [class.vendor-radio--selected]="
                    selectedVendorId === vendor.id
                  "
                >
                  <i></i>
                </span>
                <span>{{ vendor.name }}</span>
                <span
                  >{{ vendor.townCity || vendor.state }},
                  {{ vendor.country }}</span
                >
                <span>{{ vendor.contactPerson }}</span>
                <span>{{ vendor.telephone }}</span>
                <span>{{ vendor.email }}</span>
              </div>
              <div class="vendor-empty" *ngIf="!filteredVendorDealers.length">
                No vendors or dealers found
              </div>
            </div>
          </div>
        </ng-container>

        <div
          class="disbursement-row"
          [class.disbursement-row--borrower]="
            isAssetFinancing && !isApprovedVendorDisbursement
          "
        >
          <mat-form-field
            *ngIf="isAssetFinancing && !isApprovedVendorDisbursement"
            class="disbursement-method"
            appearance="outline"
          >
            <mat-label>Disbursement Method</mat-label>
            <mat-select formControlName="disbursementMethod">
              <mat-option
                *ngFor="let method of disbursementMethods"
                [value]="method.value"
              >
                {{ method.label }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field class="disbursement-transfer" appearance="outline">
            <mat-label>Transfer Type</mat-label>
            <mat-select formControlName="transferType">
              <mat-select-trigger>
                {{
                  selectedTransferType === "SAVINGS_ACCOUNT"
                    ? organizationName
                    : selectedTransferTypeLabel
                }}
              </mat-select-trigger>
              <mat-option
                *ngFor="let type of availableTransferTypes"
                [value]="type.value"
              >
                {{ type.label }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <ng-container *ngIf="selectedTransferType === 'SAVINGS_ACCOUNT'">
            <!-- Reads back the client's own savings account rather than offering a
                 picker, so it is a text field in both transfer types. The
                 savingsAccountId control still holds the account (defaulted to
                 ownAccount[0] in initForm), so the narration, Account Name and
                 the MFB "savings account required" check are unaffected.

                 readonly, never disabled: it renders as a live field rather
                 than a greyed one. Its value is a one-way binding off
                 savingsAccountId, so typing into it would be discarded. -->
            <mat-form-field class="disbursement-account" appearance="outline">
              <mat-label>Account to Credit</mat-label>
              <input
                matInput
                [value]="selectedTenantAccountNumber"
                readonly
                required
              />
            </mat-form-field>
          </ng-container>

          <ng-container *ngIf="selectedTransferType === 'THIRD_PARTY'">
            <mat-form-field
              class="disbursement-account"
              appearance="outline"
              [class.mat-form-field-invalid]="errorMsg"
            >
              <mat-label>Account to Credit <em class="req">*</em></mat-label>
              <input
                matInput
                formControlName="accountNumber"
                type="tel"
                appStrictNumbers
                maxlength="10"
                required
              />
            </mat-form-field>

            <mat-form-field class="disbursement-bank" appearance="outline">
              <mat-label>Bank Name</mat-label>
              <mat-select formControlName="bankId" required>
                <mat-select-trigger>
                  <span class="disbursement-bank-value">
                    <img
                      *ngIf="showFirstBankMark"
                      src="assets/images/first-bank-mark.png"
                      alt=""
                      aria-hidden="true"
                    />
                    <span>{{ selectedDisbursementBankName }}</span>
                  </span>
                </mat-select-trigger>
                <mat-option>
                  <ngx-mat-select-search
                    [formControl]="bankSearch"
                    placeholderLabel="Search bank..."
                    noEntriesFoundLabel="No matching bank"
                  ></ngx-mat-select-search>
                </mat-option>
                <mat-option
                  *ngFor="
                    let bank of banks | filterOptions: bankSearch.value || ''
                  "
                  [value]="bank.code || bank.id"
                >
                  {{ bank.name }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </ng-container>

          <!-- Figma 16032:25294 / 16032:25289 — Account Name and Currency close
               this row in both transfer types, both read-backs. -->
          <mat-form-field
            class="disbursement-name disbursement-disabled"
            [class.disbursement-name--tenant]="
              selectedTransferType === 'SAVINGS_ACCOUNT'
            "
            appearance="outline"
          >
            <mat-label
              >Account Name
              <em *ngIf="selectedTransferType === 'SAVINGS_ACCOUNT'" class="req"
                >*</em
              ></mat-label
            >
            <input
              matInput
              [value]="
                selectedTransferType === 'SAVINGS_ACCOUNT'
                  ? tenantAccountName
                  : disbursementForm.get('accountName').value
              "
              disabled
            />
            <mat-error *ngIf="errorMsg">{{ errorMsg }}</mat-error>
          </mat-form-field>

          <mat-form-field
            *ngIf="selectedTransferType === 'SAVINGS_ACCOUNT'"
            class="disbursement-account-type disbursement-disabled"
            appearance="outline"
          >
            <mat-label>Account Type</mat-label>
            <input matInput value="Savings Account" disabled />
          </mat-form-field>

          <mat-form-field
            class="disbursement-currency disbursement-disabled"
            appearance="outline"
          >
            <mat-label>Currency</mat-label>
            <input matInput [value]="loanCurrency" disabled />
          </mat-form-field>
        </div>

        <div
          class="disbursement-row"
          [class.disbursement-row--borrower-secondary]="
            isAssetFinancing && !isApprovedVendorDisbursement
          "
        >
          <mat-form-field
            class="disbursement-amount disbursement-disabled"
            appearance="outline"
          >
            <mat-label
              >Amount
              <em *ngIf="selectedTransferType === 'SAVINGS_ACCOUNT'" class="req"
                >*</em
              ></mat-label
            >
            <input
              matInput
              [value]="getFormattedCurrency(basicForm.get('principal').value)"
              disabled
              required
            />
          </mat-form-field>

          <!-- Takes the rest of the row: Repayment Account no longer sits beside
               it - the loan's repayments follow the product (see
               repaymentAccountId in the component). -->
          <mat-form-field
            class="disbursement-narration disbursement-disabled"
            appearance="outline"
          >
            <mat-label>Narration</mat-label>
            <input matInput [value]="disbursementNarration" disabled />
          </mat-form-field>
        </div>
      </div>
    </form>

    <!-- Guarantor Form — Figma node 15281:24629 -->
    <form
      [formGroup]="guarantorForm"
      class="guarantor-form"
      [hidden]="activeTab !== 'guarantor'"
    >
      <!-- Financial Identifier + warning above it (Figma 15281:24546 / 15284:24479).
           The warning is offset to start at the identifier input's left edge (105px),
           i.e. flush with the right border of the identifier-type select. -->
      <div class="guarantor-identifier-block">
        <div class="guarantor-identifier-warning">
          <span class="guarantor-identifier-warning-badge">!</span>
          <span i18n="@@usePhoneForExistingGuarantors"
            >Use telephone number for existing guarantors</span
          >
        </div>
        <ng-container *ngIf="guarantorForm.get('bvn') as bvn">
          <div class="guarantor-identifier-row">
            <div class="guarantor-identifier-field-wrap">
              <span
                class="guarantor-identifier-floating-label"
                i18n="@@financialIdentifierLabel"
                >Financial Identifier</span
              >
              <mat-form-field
                appearance="outline"
                class="w-full custom-form-field guarantor-identifier-field"
              >
                <input type="tel" matInput [formControl]="searchInput" />
                <span
                  matPrefix
                  class="guarantor-identifier-type-wrapper"
                  (click)="$event.stopPropagation()"
                >
                  <mat-select
                    class="guarantor-identifier-type-select"
                    panelClass="guarantor-identifier-type-panel"
                    formControlName="identifierType"
                  >
                    <mat-option
                      *ngFor="let opt of identifierTypeOptions"
                      [value]="opt"
                      >{{ opt }}</mat-option
                    >
                  </mat-select>
                </span>
              </mat-form-field>
            </div>
            <button
              type="button"
              (click)="verifyGuarantorIdentity()"
              class="guarantor-identifier-search-btn"
              [disabled]="!searchInput.value || verifyingIdentity"
            >
              <mat-spinner
                *ngIf="verifyingIdentity"
                diameter="18"
                strokeWidth="1"
              ></mat-spinner>
              <mat-icon *ngIf="!verifyingIdentity">search</mat-icon>
            </button>
          </div>
        </ng-container>
      </div>

      <!-- Bio Data / Residential / Business sections — 28px apart (Figma 15284:24452) -->
      <div class="guarantor-sections">
        <!-- Bio Data section -->
        <div class="guarantor-section">
          <span class="guarantor-section-title" i18n="@@bio_data"
            >Bio Data</span
          >
          <!-- TODO: verification badge — re-enable when design is confirmed
          <ng-container *ngIf="foundGuarantorId">
            <span *ngIf="foundGuarantorIdentifierVerified || hasVerifiedIdentity"
              class="flex items-center gap-[3px] text-[0.625rem] font-medium text-green-600 bg-green-50 border border-green-200 rounded-full px-2 py-[2px]">
              <mat-icon style="font-size: 10px; width: 10px; height: 10px; line-height: 10px;">check_circle</mat-icon>
              <span i18n="@@identityVerified">Identity Verified</span>
            </span>
            <span *ngIf="!foundGuarantorIdentifierVerified && !hasVerifiedIdentity"
              class="flex items-center gap-[3px] text-[0.625rem] font-medium text-error bg-error/10 border border-error/20 rounded-full px-2 py-[2px]">
              <mat-icon style="font-size: 10px; width: 10px; height: 10px; line-height: 10px;">warning</mat-icon>
              <span i18n="@@identityNotVerified">Identity Not Verified</span>
            </span>
          </ng-container>
          -->

          <div class="guarantor-card guarantor-card--biodata">
            <!-- Names + contact rows share the left column; the photo tile sits
                 alongside them, bottom-aligned (Figma 15283:25293). -->
            <div class="guarantor-biodata-top">
              <div class="guarantor-biodata-fields">
                <!-- First / Middle / Last name -->
                <div class="guarantor-row guarantor-row--names">
                  <mat-form-field
                    appearance="outline"
                    subscriptSizing="dynamic"
                  >
                    <mat-label i18n="@@firstNameLabel">First Name</mat-label>
                    <input
                      appRemoveSpaces
                      type="text"
                      formControlName="firstName"
                      matInput
                    />
                  </mat-form-field>
                  <mat-form-field
                    appearance="outline"
                    subscriptSizing="dynamic"
                  >
                    <mat-label i18n="@@middleNameLabel">Middle Name</mat-label>
                    <input
                      appRemoveSpaces
                      type="text"
                      formControlName="middleName"
                      matInput
                    />
                  </mat-form-field>
                  <mat-form-field
                    appearance="outline"
                    subscriptSizing="dynamic"
                  >
                    <mat-label i18n="@@lastNameLabel">Last Name</mat-label>
                    <input
                      appRemoveSpaces
                      type="text"
                      formControlName="lastName"
                      matInput
                    />
                  </mat-form-field>
                </div>

                <!-- Gender | Phone | Alternate Phone | national identifier -->
                <div class="guarantor-row guarantor-row--contact">
                  <mat-form-field
                    class="guarantor-gender-field"
                    appearance="outline"
                  >
                    <mat-label i18n="@@genderLabel">Gender</mat-label>
                    <mat-select formControlName="gender">
                      <mat-option value="MALE" i18n="@@maleOption"
                        >Male</mat-option
                      >
                      <mat-option value="FEMALE" i18n="@@femaleOption"
                        >Female</mat-option
                      >
                      <mat-option value="OTHER" i18n="@@otherOption"
                        >Other</mat-option
                      >
                    </mat-select>
                  </mat-form-field>

                  <!-- Phone Number -->
                  <mat-form-field
                    class="guarantor-phone-field"
                    appearance="outline"
                  >
                    <mat-label i18n="@@phoneNumberLabel"
                      >Phone Number</mat-label
                    >
                    <input
                      class="hidden"
                      value="prevent_mat_form_throwing_missing_matInput_errors"
                      matInput
                    />
                    <ngx-intl-tel-input
                      appRemoveLeadingZero
                      class="z-40"
                      [cssClass]="'w-full bg-transparent'"
                      [preferredCountries]="preferredCountries"
                      [enableAutoCountrySelect]="true"
                      [enablePlaceholder]="false"
                      [searchCountryFlag]="true"
                      [selectFirstCountry]="false"
                      [selectedCountryISO]="currencyFormatter.tenantCountryISO"
                      [maxLength]="15"
                      [phoneValidation]="true"
                      [numberFormat]="PhoneNumberFormat.National"
                      name="mobile number"
                      formControlName="mobileNumber"
                    ></ngx-intl-tel-input>
                    <span matSuffix class="guarantor-default-slot">
                      <small
                        class="guarantor-default-label"
                        i18n="@@defaultLabel"
                        >Default</small
                      >
                      <input
                        type="radio"
                        name="defaultPhoneSlot"
                        value="mobileNumber"
                        class="guarantor-default-radio"
                        [checked]="
                          guarantorForm.get('defaultPhoneSlot')?.value ===
                          'mobileNumber'
                        "
                        (change)="
                          guarantorForm
                            .get('defaultPhoneSlot')
                            .setValue('mobileNumber')
                        "
                      />
                    </span>
                    <mat-hint i18n="@@excludeLeadingZeroHint"
                      >Exclude leading zero (0)</mat-hint
                    >
                  </mat-form-field>

                  <!-- Alternate Phone Number -->
                  <mat-form-field
                    class="guarantor-phone-field"
                    appearance="outline"
                  >
                    <mat-label i18n="@@alternatephoneNumberLabel"
                      >Alternate Phone Number</mat-label
                    >
                    <input
                      class="hidden"
                      value="prevent_mat_form_throwing_missing_matInput_errors"
                      matInput
                    />
                    <ngx-intl-tel-input
                      appRemoveLeadingZero
                      class="z-40"
                      [cssClass]="'w-full bg-transparent'"
                      [preferredCountries]="preferredCountries"
                      [enableAutoCountrySelect]="true"
                      [enablePlaceholder]="false"
                      [searchCountryFlag]="true"
                      [selectFirstCountry]="false"
                      [selectedCountryISO]="currencyFormatter.tenantCountryISO"
                      [maxLength]="15"
                      [phoneValidation]="true"
                      [numberFormat]="PhoneNumberFormat.National"
                      name="alternate phone number"
                      formControlName="alternatePhoneNumber"
                    ></ngx-intl-tel-input>
                    <span matSuffix class="guarantor-default-slot">
                      <small
                        class="guarantor-default-label"
                        i18n="@@defaultLabel"
                        >Default</small
                      >
                      <input
                        type="radio"
                        name="defaultPhoneSlot"
                        value="alternatePhoneNumber"
                        class="guarantor-default-radio"
                        [checked]="
                          guarantorForm.get('defaultPhoneSlot')?.value ===
                          'alternatePhoneNumber'
                        "
                        (change)="
                          guarantorForm
                            .get('defaultPhoneSlot')
                            .setValue('alternatePhoneNumber')
                        "
                      />
                    </span>
                    <mat-hint i18n="@@excludeLeadingZeroHint"
                      >Exclude leading zero (0)</mat-hint
                    >
                  </mat-form-field>

                  <!-- Locked once the identifier is verified, or when it comes
                       from the verification search. Editable when the tenant
                       has verification off, or for a guarantor found by phone
                       whose identifier is still unverified. -->
                  <mat-form-field
                    class="guarantor-bvn-field"
                    [class.guarantor-bvn-field--locked]="bvnLocked"
                    appearance="outline"
                  >
                    <mat-label>{{ guarantorIdentifierLabel }}</mat-label>
                    <input
                      matInput
                      [readonly]="bvnLocked"
                      formControlName="bvn"
                      class="bg-transparent"
                    />
                    <mat-spinner
                      *ngIf="
                        verifyingIdentity &&
                        foundGuarantorId &&
                        !foundGuarantorIdentifierVerified
                      "
                      matSuffix
                      diameter="16"
                      strokeWidth="2"
                    ></mat-spinner>
                  </mat-form-field>
                </div>
              </div>

              <!-- Photo tile — 100x91, bottom-aligned with the contact row (Figma 15283:25288) -->
              <app-file-upload-ui
                class="guarantor-photo-tile"
                classNames="guarantor-upload-box guarantor-upload-box--photo"
                label="Photo"
                i18n-label="@@photoLabel"
                hint=".png, .jpg, .bmp (Max. 5MB)"
                [currentPhoto]="imageValues?.photo"
                [disabled]="isViewMode"
                (upload)="saveFile($event, 'photo')"
              ></app-file-upload-ui>
            </div>

            <!-- Date of Birth | Occupation | Email -->
            <div class="guarantor-row guarantor-row--personal">
              <mat-form-field class="guarantor-dob-field" appearance="outline">
                <mat-label i18n="@@dateOfBirthLabel">Date of Birth</mat-label>
                <input
                  readonly
                  [max]="dobAgeRange"
                  matInput
                  [matDatepicker]="dobPicker"
                  placeholder="Choose a date"
                  i18n-placeholder="@@chooseDatePlaceholder"
                  formControlName="dateOfBirth"
                />
                <mat-datepicker-toggle
                  matSuffix
                  [for]="dobPicker"
                ></mat-datepicker-toggle>
                <mat-datepicker #dobPicker></mat-datepicker>
                <mat-hint i18n="@@ageRangeHint">Age range 18-80</mat-hint>
              </mat-form-field>
              <mat-form-field
                class="guarantor-occupation-field"
                appearance="outline"
              >
                <mat-label i18n="@@occupationLabel">Occupation</mat-label>
                <input
                  appRemoveSpaces
                  type="text"
                  formControlName="occupation"
                  matInput
                />
              </mat-form-field>
              <mat-form-field
                class="guarantor-email-field"
                appearance="outline"
              >
                <mat-label i18n="@@emailAddressLabel">Email Address</mat-label>
                <input
                  appRemoveSpaces
                  type="text"
                  formControlName="email"
                  matInput
                />
              </mat-form-field>
            </div>

            <!-- Signature | ID Card -->
            <div class="guarantor-row guarantor-row--uploads">
              <app-file-upload-ui
                classNames="guarantor-upload-box"
                label="Signature"
                i18n-label="@@signatureLabel"
                hint=".png, .jpg, .bmp (Max. 2 MB)"
                [currentPhoto]="imageValues?.signature"
                [disabled]="isViewMode"
                (upload)="saveFile($event, 'signature')"
              ></app-file-upload-ui>
              <app-file-upload-ui
                classNames="guarantor-upload-box"
                label="ID Card"
                i18n-label="@@idCardLabel"
                hint=".png, .jpg, .bmp (Max. 2 MB)"
                [currentPhoto]="imageValues?.idCard"
                [disabled]="isViewMode"
                (upload)="saveFile($event, 'idCard')"
              ></app-file-upload-ui>
            </div>
          </div>
        </div>

        <!-- Residential Address section -->
        <div class="guarantor-section">
          <span class="guarantor-section-title" i18n="@@residentialAddress"
            >Residential Address</span
          >
          <div class="guarantor-card">
            <div class="guarantor-row guarantor-row--address">
              <app-searchable-select
                label="Country"
                i18n-label="@@countryLabel"
                [options]="countries"
                formControlName="countryId"
              ></app-searchable-select>
              <app-searchable-select
                [label]="residentialStateLabel"
                [options]="states"
                formControlName="stateId"
              ></app-searchable-select>
              <app-searchable-select
                [label]="residentialLgaLabel"
                [options]="localGovernments"
                formControlName="lgaId"
              ></app-searchable-select>
              <mat-form-field appearance="outline">
                <mat-label i18n="@@townCityLabel">Town/City</mat-label>
                <input
                  appRemoveSpaces
                  type="text"
                  formControlName="townName"
                  matInput
                />
              </mat-form-field>
            </div>
            <div class="guarantor-row guarantor-row--address-lines">
              <mat-form-field appearance="outline">
                <mat-label i18n="@@addressLine1Label">Address 1</mat-label>
                <input
                  appRemoveSpaces
                  type="text"
                  formControlName="addressLine1"
                  matInput
                />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label i18n="@@addressLine2Label">Address 2</mat-label>
                <input
                  appRemoveSpaces
                  type="text"
                  formControlName="addressLine2"
                  matInput
                />
              </mat-form-field>
            </div>
          </div>
        </div>

        <!-- Business Address section -->
        <div class="guarantor-section">
          <span class="guarantor-section-title" i18n="@@businessAddress"
            >Business Address</span
          >
          <div class="guarantor-card">
            <div class="guarantor-row guarantor-row--address">
              <app-searchable-select
                label="Country"
                i18n-label="@@countryLabel"
                [options]="countries"
                formControlName="businessCountryId"
              ></app-searchable-select>
              <app-searchable-select
                [label]="businessStateLabel"
                [options]="businessStates"
                formControlName="businessStateId"
              ></app-searchable-select>
              <app-searchable-select
                [label]="businessLgaLabel"
                [options]="businessLocalGovernments"
                formControlName="businessLgaId"
              ></app-searchable-select>
              <mat-form-field appearance="outline">
                <mat-label i18n="@@townCityLabel">Town/City</mat-label>
                <input
                  appRemoveSpaces
                  type="text"
                  formControlName="businessTownName"
                  matInput
                />
              </mat-form-field>
            </div>
            <div class="guarantor-row guarantor-row--address-lines">
              <mat-form-field appearance="outline">
                <mat-label i18n="@@businessAddressLine1Label"
                  >Address 1</mat-label
                >
                <input
                  appRemoveSpaces
                  type="text"
                  formControlName="businessAddressLine1"
                  matInput
                />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label i18n="@@businessAddressLine2Label"
                  >Address 2</mat-label
                >
                <input
                  appRemoveSpaces
                  type="text"
                  formControlName="businessAddressLine2"
                  matInput
                />
              </mat-form-field>
            </div>
          </div>
        </div>
      </div>
    </form>

    <!-- Action Buttons -->
    <div class="flex items-center justify-center gap-3 my-3 px-[10px] md:px-0">
      <!-- Basic Tab Buttons -->
      <ng-container *ngIf="activeTab === 'basic'">
        <app-button-icon-ui
          class="!w-[120px] md:flex-none md:!w-[100px]"
          classNames="btn border-error text-error bg-white w-full"
          name="Cancel"
          value="Cancel"
          i18n-name="@@cancel"
          tooltip="Cancel"
          i18n-tooltip="@@cancelTooltip"
          (clickEmit)="close()"
        ></app-button-icon-ui>
        <app-button-icon-ui
          [isDisabled]="basicForm.invalid"
          [iconPosition]="'right'"
          [icon]="'chevron_right'"
          class="!w-[120px] md:flex-none md:!w-[100px]"
          classNames="btn w-full text-white bg-primary"
          [name]="primaryActionLabelBasic"
          value="next"
          [tooltip]="
            isDirectDisburseFromBasic
              ? 'Submit'
              : loanProductRecords?.requireGuarantor
                ? 'Move to guarantor details'
                : 'Move to disbursement details'
          "
          (clickEmit)="onSubmitBasic()"
        ></app-button-icon-ui>
      </ng-container>

      <!-- Guarantor Tab Buttons -->
      <ng-container *ngIf="activeTab === 'guarantor'">
        <app-button-icon-ui
          class="!w-[120px] md:flex-none md:!w-[100px]"
          classNames="btn border-error text-error bg-white w-full"
          name="Cancel"
          value="Cancel"
          i18n-name="@@cancelButton"
          tooltip="Cancel"
          i18n-tooltip="@@cancelTooltip"
          (clickEmit)="close()"
        ></app-button-icon-ui>
        <app-button-icon-ui
          *ngIf="canEditBioData && (hasVerifiedIdentity || foundGuarantorId)"
          class="!w-[120px] md:flex-none md:!w-[100px]"
          classNames="btn border-primary text-primary bg-white w-full"
          [name]="isBioDataEditing ? 'Lock Bio' : 'Edit Bio'"
          value="editBio"
          [tooltip]="isBioDataEditing ? 'Lock bio data' : 'Edit bio data'"
          (clickEmit)="
            isBioDataEditing ? disableBioDataFields() : enableBioDataFields()
          "
        ></app-button-icon-ui>
        <app-button-icon-ui
          [iconPosition]="'left'"
          [icon]="'chevron_left'"
          class="!w-[120px] md:flex-none md:!w-[100px]"
          classNames="btn w-full text-white bg-primary"
          name="Previous"
          value="previous"
          i18n-name="@@previousButton"
          tooltip="Back to basic details"
          (clickEmit)="gotoBasic()"
        ></app-button-icon-ui>
        <app-button-icon-ui
          [isDisabled]="guarantorForm.invalid"
          [iconPosition]="'right'"
          [icon]="'chevron_right'"
          class="!w-[120px] md:flex-none md:!w-[100px]"
          classNames="btn w-full text-white bg-primary"
          [name]="primaryActionLabelGuarantor"
          value="next"
          [tooltip]="
            isDirectDisburseFromGuarantor
              ? 'Submit'
              : 'Move to disbursement details'
          "
          (clickEmit)="onSubmitGuarantor()"
        ></app-button-icon-ui>
      </ng-container>

      <!-- Disbursement Details Tab Buttons -->
      <ng-container *ngIf="activeTab === 'disbursementDetails'">
        <app-button-icon-ui
          class="!w-[120px] md:flex-none md:!w-[120px]"
          classNames="btn border-error text-error bg-white w-full"
          name="Cancel"
          value="Cancel"
          i18n-name="@@cancelButton"
          tooltip="Cancel"
          i18n-tooltip="@@cancelTooltip"
          (clickEmit)="close()"
        ></app-button-icon-ui>
        <app-button-icon-ui
          [iconPosition]="'left'"
          [icon]="'chevron_left'"
          class="!w-[120px] md:flex-none md:!w-[120px]"
          classNames="btn w-full text-white bg-primary"
          name="Previous"
          value="previous"
          i18n-name="@@previousButton"
          [tooltip]="
            loanProductRecords?.requireGuarantor
              ? 'Back to guarantor details'
              : 'Back to basic details'
          "
          (clickEmit)="
            loanProductRecords?.requireGuarantor ? gotoGuarantor() : gotoBasic()
          "
        ></app-button-icon-ui>
        <app-button-icon-ui
          [isDisabled]="disbursementForm.invalid"
          class="!w-[120px] md:flex-none md:!w-[120px]"
          classNames="btn w-full text-white bg-success"
          name="Save"
          value="submit"
          i18n-name="@@save"
          tooltip="Submit loan application"
          i18n-tooltip="@@submitTooltip"
          (clickEmit)="onSubmitDisbursementDetails()"
        ></app-button-icon-ui>
      </ng-container>
    </div>
  </div>
</div>
